import React from 'react'
import { ActivityIndicator, Text } from 'react-native'
import {
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow
} from '@material-ui/core'
import { parseDateString } from '../utils/policyDispatchingUtils'

type Props = {
  open: boolean
  loading: boolean
  rows: any[]
  title: string
  headCell: { id: number; label: string }[]
  rowCell: { id: number; label: string }[]
  keySubmit: string
  nameFilterField?: string
  onClose: () => void
  onSelect: (authorizedPerson: string) => void
}

export default function ChoosePersonDialog({
  open,
  loading,
  title,
  rows,
  headCell,
  rowCell,
  keySubmit,
  nameFilterField,
  onClose,
  onSelect
}: Props) {
  const [nameSearchInput, setNameSearchInput] = React.useState('')
  const [appliedNameSearch, setAppliedNameSearch] = React.useState('')

  React.useEffect(() => {
    if (open) {
      setNameSearchInput('')
      setAppliedNameSearch('')
    }
  }, [open, nameFilterField])

  const filteredRows =
    nameFilterField && appliedNameSearch
      ? rows.filter((row) =>
          String(row[nameFilterField] || '')
            .toLocaleLowerCase()
            .includes(appliedNameSearch)
        )
      : rows

  const applyNameSearch = () => setAppliedNameSearch(nameSearchInput.trim().toLocaleLowerCase())

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <style>{`
        .choose-person-row {
          cursor: pointer;
          transition: background-color 120ms ease;
        }

        .choose-person-row:not(.choose-person-row-disabled):hover {
          background-color: rgba(237, 27, 46, 0.08);
        }

        .choose-person-row-disabled {
          opacity: 0.55;
          cursor: not-allowed;
        }
      `}</style>
      <DialogTitle>
        <Text style={{ fontWeight: '600', fontSize: 20 }}>{title}</Text>
        <IconButton aria-label="Đóng" onClick={onClose} style={{ position: 'absolute', right: 8, top: 8 }}>
          ×
        </IconButton>
      </DialogTitle>
      <DialogContent style={{ padding: '0 24px 24px' }}>
        {nameFilterField && (
          <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
            <input
              type="text"
              value={nameSearchInput}
              placeholder="Nhập tên người quản lý khu vực"
              onChange={(event) => setNameSearchInput(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === 'Enter') {
                  applyNameSearch()
                }
              }}
              style={{
                flex: 1,
                minWidth: 0,
                height: 40,
                boxSizing: 'border-box',
                border: '1px solid #d1d5db',
                borderRadius: 6,
                padding: '0 12px',
                fontSize: 14
              }}
            />
            <button
              type="button"
              onClick={applyNameSearch}
              style={{
                border: 0,
                borderRadius: 6,
                padding: '0 16px',
                backgroundColor: '#ed1b2e',
                color: '#fff',
                fontWeight: 600,
                cursor: 'pointer'
              }}
            >
              Tìm
            </button>
          </div>
        )}
        {loading ? (
          <ActivityIndicator size="large" color="#ED1B2E" />
        ) : (
          <div style={{ border: '1px solid #d9e0ea', borderRadius: 10, overflow: 'hidden' }}>
            <Table style={{ borderCollapse: 'separate', borderSpacing: 0 }}>
              <TableHead>
                <TableRow>
                  {headCell.map(({ id, label }) => (
                    <TableCell
                      key={id}
                      style={{
                        backgroundColor: '#ed1b2e',
                        color: '#fff',
                        fontWeight: 700,
                        fontSize: 14,
                        padding: '11px 12px',
                        whiteSpace: 'nowrap'
                      }}
                    >
                      {label}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredRows.map((row) => {
                  const expiryDate = parseDateString(row.expiryDate)
                  const today = new Date()
                  today.setHours(0, 0, 0, 0)
                  const isExpired = Boolean(
                    expiryDate &&
                      new Date(expiryDate.getFullYear(), expiryDate.getMonth(), expiryDate.getDate()) < today
                  )

                  return (
                    <TableRow
                      key={row.id}
                      className={`choose-person-row${isExpired ? ' choose-person-row-disabled' : ''}`}
                      onClick={() => {
                        if (!isExpired) onSelect(row[keySubmit])
                      }}
                      onKeyDown={(event) => {
                        if (!isExpired && (event.key === 'Enter' || event.key === ' ')) {
                          event.preventDefault()
                          onSelect(row[keySubmit])
                        }
                      }}
                      role={isExpired ? undefined : 'button'}
                      tabIndex={isExpired ? undefined : 0}
                      aria-disabled={isExpired}
                    >
                      {rowCell.map(({ id, label }) => {
                        if (label === 'id') {
                          return (
                            <TableCell key={id} style={{ padding: '10px 12px', borderBottom: '1px solid #e7ebf0' }}>
                              <span style={{ color: '#ed1b2e', textDecoration: 'underline', fontSize: 14 }}>
                                {row[label]}
                              </span>
                            </TableCell>
                          )
                        }

                        return (
                          <TableCell key={id} style={{ padding: '10px 12px', borderBottom: '1px solid #e7ebf0' }}>
                            {row[label]}
                          </TableCell>
                        )
                      })}
                    </TableRow>
                  )
                })}
                {filteredRows.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={6} align="center" style={{ padding: 20, color: '#6b7280' }}>
                      Không tìm được thông tin theo yêu cầu
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        )}
      </DialogContent>
    </Dialog>
  )
}
